import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Form,
  Badge,
  Spinner,
  Container,
  Row,
  Col,
  Card,
  Modal,
  Pagination,
} from "react-bootstrap";
import { FiEdit2 } from "react-icons/fi";
import { GrView } from "react-icons/gr";
import { RiDeleteBinLine } from "react-icons/ri";
import defaultTemplateImage from "../../assets/images/template-default.png";
import NurturixCustomTabs from "../../components/common/NurturixCustomTabs";
import PrimaryButton from "../../components/common/PrimaryButton";
import SecondaryButton from "../../components/common/SecondaryButton";
import TemplateService, { TEMPLATE_CREATOR_TYPES } from "../../services/TemplateService";
import UserSessionManager from "../../utils/UserSessionManager";
import {
  showDeleteConfirm,
  showErrorAlert,
  showSuccessAlert,
} from "../../utils/sweetAlertUtils";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const SORT_OPTIONS = [
  { value: "name-asc", sortBy: "name", sortOrder: "asc", label: "Name (A-Z)" },
  { value: "name-desc", sortBy: "name", sortOrder: "desc", label: "Name (Z-A)" },
  { value: "category-asc", sortBy: "templateCategory", sortOrder: "asc", label: "Category (A-Z)" },
  { value: "category-desc", sortBy: "templateCategory", sortOrder: "desc", label: "Category (Z-A)" },
];

const TABS = [
  { key: "admin", label: "Admin Templates" },
  { key: "superAdmin", label: "Sample Available Templates" },
];

const getCategoryId = (categoryName) => {
  const categories = {
    Marketing: 1,
    Education: 2,
    Health: 3,
    Property: 4,
  };
  return categories[categoryName] || 0;
};

const buildUpdateFormData = (template, overrides = {}) => {
  const formData = new FormData();
  formData.append("Id", template.id);
  formData.append("Name", overrides.name ?? template.name);
  formData.append("TemplateBody", overrides.templateBody ?? template.templateBody);
  formData.append("TemplateDetails", overrides.templateDetails ?? template.templateDetails ?? "test");
  formData.append(
    "TemplateCategoryId",
    overrides.templateCategoryId ?? getCategoryId(template.templateCategory),
  );
  formData.append("IsActive", overrides.isActive ?? template.active);
  return formData;
};

const getInitialTabState = () => ({
  templates: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize: 10,
  search: "",
  searchInput: "",
  sortValue: "name-asc",
  loading: false,
});

const CARD_WIDTH = 220;
const CARD_HEIGHT = 150;

const TemplatePreview = ({ template, width = CARD_WIDTH, height = CARD_HEIGHT, isCardTop = false }) => {
  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;
  const backgroundImage = template.templateImage
    ? `${BASE_URL}${template.templateImage}`
    : defaultTemplateImage;

  const templateBodyRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [imageSize, setImageSize] = useState({ width: 900, height: 400 });

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = backgroundImage;
    img.onload = () => setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => setImageSize({ width: 900, height: 400 });
  }, [backgroundImage]);

  useEffect(() => {
    if (imageSize.width === 0 || imageSize.height === 0) return;
    const scaleX = width / imageSize.width;
    const scaleY = height / imageSize.height;
    setScale(Math.min(scaleX, scaleY));
  }, [imageSize, width, height]);

  return (
    <div
      style={{
        width: `${width}px`,
        height: `${height}px`,
        position: "relative",
        overflow: "hidden",
        border: "1px solid #d1d5db",
        borderRadius: isCardTop ? "6px 6px 0 0" : "6px",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          width: `${imageSize.width}px`,
          height: `${imageSize.height}px`,
          position: "absolute",
          top: 0,
          left: 0,
          overflow: "hidden",
        }}
      >
        <div
          ref={templateBodyRef}
          dangerouslySetInnerHTML={{ __html: template.templateBody }}
          style={{
            backgroundImage: `url("${encodeURI(backgroundImage)}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: "100%",
            height: "100%",
            position: "relative",
            padding: 0,
            margin: 0,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        />
      </div>
    </div>
  );
};

const TemplateViewModal = ({ template, show, onHide, onUseTemplate, showUseButton }) => {
  if (!template) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{template.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column flex-md-row gap-4 align-items-start">
          <TemplatePreview template={template} width={320} height={220} />
          <div>
            <p className="mb-2">
              <strong>Category:</strong> {template.templateCategory || "—"}
            </p>
            {template.active !== undefined && (
              <p className="mb-2">
                <strong>Status:</strong>{" "}
                <Badge bg={template.active ? "success" : "secondary"}>
                  {template.active ? "Active" : "Inactive"}
                </Badge>
              </p>
            )}
            {template.templateDetails && (
              <p className="mb-0 text-muted small">{template.templateDetails}</p>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex gap-2 justify-content-end">
        <SecondaryButton onClick={onHide}>Close</SecondaryButton>
        {showUseButton && (
          <PrimaryButton
            onClick={() => {
              onUseTemplate?.(template);
              onHide();
            }}
          >
            Use This Template
          </PrimaryButton>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const TemplateToolbar = ({
  searchInput,
  onSearchInputChange,
  onSearchSubmit,
  sortValue,
  onSortChange,
  pageSize,
  onPageSizeChange,
  totalCount,
}) => (
  <Form
    onSubmit={(e) => {
      e.preventDefault();
      onSearchSubmit();
    }}
    className="d-flex flex-column flex-lg-row gap-3 mb-3 align-items-lg-end"
  >
    <div className="flex-grow-1">
      <Form.Label className="small text-muted mb-1">Search</Form.Label>
      <div className="d-flex gap-2">
        <Form.Control
          type="text"
          placeholder="Search by name or category..."
          value={searchInput}
          onChange={(e) => onSearchInputChange(e.target.value)}
        />
        <PrimaryButton
          type="submit"
          className="px-3"
          style={{ fontSize: "0.9rem", padding: "0.5rem 1.25rem" }}
        >
          Search
        </PrimaryButton>
      </div>
    </div>
    <div style={{ minWidth: 180 }}>
      <Form.Label className="small text-muted mb-1">Sort by</Form.Label>
      <Form.Select value={sortValue} onChange={(e) => onSortChange(e.target.value)}>
        {SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </div>
    <div style={{ minWidth: 120 }}>
      <Form.Label className="small text-muted mb-1">Per page</Form.Label>
      <Form.Select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
        {PAGE_SIZE_OPTIONS.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </Form.Select>
    </div>
    <div className="text-muted small pb-1">
      {totalCount} template{totalCount === 1 ? "" : "s"}
    </div>
  </Form>
);

const PaginationControls = ({ pageNumber, totalPages, onPageChange, loading }) => (
  <div className="d-flex justify-content-between align-items-center mt-3">
    <div className="text-muted small">
      Page {totalPages === 0 ? 0 : pageNumber} of {totalPages || 1}
    </div>
    <Pagination className="mb-0">
      <Pagination.Prev
        disabled={loading || pageNumber <= 1}
        onClick={() => onPageChange(pageNumber - 1)}
      />
      <Pagination.Next
        disabled={loading || pageNumber >= totalPages}
        onClick={() => onPageChange(pageNumber + 1)}
      />
    </Pagination>
  </div>
);

const groupTemplatesByCategory = (templates) =>
  templates.reduce((acc, t) => {
    const category = t.templateCategory || "Uncategorized";
    acc[category] = acc[category] || [];
    acc[category].push(t);
    return acc;
  }, {});

const TemplateCardGrid = ({ templates, emptyMessage, renderCardFooter }) => {
  if (templates.length === 0) {
    return <p className="text-center text-muted py-4 mb-0">{emptyMessage}</p>;
  }

  const grouped = groupTemplatesByCategory(templates);

  return (
    <>
      {Object.keys(grouped).map((category) => (
        <div key={category} className="mb-4">
          <h6
            className="text-uppercase text-muted mb-3"
            style={{ letterSpacing: "0.05em", fontSize: "13px", fontWeight: 600 }}
          >
            {category}
          </h6>
          <div className="d-flex flex-wrap gap-3">
            {grouped[category].map((tpl) => (
              <div
                key={tpl.id}
                className="d-flex flex-column"
                style={{ width: `${CARD_WIDTH}px` }}
              >
                <TemplatePreview template={tpl} isCardTop />
                <div
                  className="border border-top-0 rounded-bottom px-2 py-2 text-center"
                  style={{ backgroundColor: "#f9fafb" }}
                >
                  <div
                    className="small fw-medium text-truncate mb-2"
                    title={tpl.name}
                  >
                    {tpl.name}
                  </div>
                  {renderCardFooter(tpl)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

const TemplateTabContent = ({
  tabKey,
  tabState,
  updateTabState,
  onSearchSubmit,
  onSortChange,
  onPageSizeChange,
  onPageChange,
  emptyMessage,
  renderCardFooter,
}) => {
  const { templates, loading, searchInput, sortValue, pageSize, pageNumber, totalCount } =
    tabState;

  return (
    <Card className="shadow-sm border-0">
      <Card.Body>
        <TemplateToolbar
          searchInput={searchInput}
          onSearchInputChange={(value) => updateTabState(tabKey, { searchInput: value })}
          onSearchSubmit={onSearchSubmit}
          sortValue={sortValue}
          onSortChange={onSortChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          totalCount={totalCount}
        />

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <TemplateCardGrid
              templates={templates}
              emptyMessage={emptyMessage}
              renderCardFooter={renderCardFooter}
            />

            {templates.length > 0 && (
              <PaginationControls
                pageNumber={pageNumber}
                totalPages={Math.ceil(totalCount / pageSize) || 1}
                onPageChange={onPageChange}
                loading={loading}
              />
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const TemplateListing = ({ onCreateNew, onSelectSample, onEditTemplate }) => {
  const isSuperAdminUser = UserSessionManager.isSuperAdmin();

  const [activeTab, setActiveTab] = useState("admin");
  const [tabState, setTabState] = useState({
    admin: getInitialTabState(),
    superAdmin: getInitialTabState(),
  });
  const tabStateRef = useRef(tabState);
  tabStateRef.current = tabState;

  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);

  const currentState = tabState[activeTab] || getInitialTabState();
  const totalPages = Math.max(1, Math.ceil(currentState.totalCount / currentState.pageSize));

  const getSortParams = (sortValue) => {
    const option = SORT_OPTIONS.find((item) => item.value === sortValue) || SORT_OPTIONS[0];
    return { sortBy: option.sortBy, sortOrder: option.sortOrder };
  };

  const buildQueryState = (tabKey, overrides = {}) => {
    const queryState = {
      ...getInitialTabState(),
      ...(tabStateRef.current[tabKey] || {}),
      ...overrides,
    };

    if (overrides.search !== undefined) {
      queryState.search = overrides.search;
    } else if (overrides.searchInput !== undefined) {
      queryState.search = overrides.searchInput;
    }

    return queryState;
  };

  const loadTemplates = async (tabKey, overrides = {}) => {
    const queryState = buildQueryState(tabKey, overrides);

    setTabState((prev) => ({
      ...prev,
      [tabKey]: { ...queryState, loading: true },
    }));

    const { sortBy, sortOrder } = getSortParams(queryState.sortValue);

    try {
      const result =
        tabKey === "superAdmin"
          ? await TemplateService.fetchSuperAdminTemplatesList({
              pageNumber: queryState.pageNumber,
              pageSize: queryState.pageSize,
              search: queryState.search,
              sortBy,
              sortOrder,
            })
          : await TemplateService.fetchTemplatesByCreatorType({
              creatorType: TEMPLATE_CREATOR_TYPES.ADMIN,
              pageNumber: queryState.pageNumber,
              pageSize: queryState.pageSize,
              search: queryState.search,
              sortBy,
              sortOrder,
            });

      setTabState((prev) => ({
        ...prev,
        [tabKey]: {
          ...getInitialTabState(),
          ...(prev[tabKey] || {}),
          ...queryState,
          templates: result.items,
          totalCount: result.totalCount,
          pageNumber: result.pageNumber,
          pageSize: result.pageSize,
          loading: false,
        },
      }));
    } catch (error) {
      console.error(`Error fetching ${tabKey} templates:`, error);
      setTabState((prev) => ({
        ...prev,
        [tabKey]: {
          ...getInitialTabState(),
          ...(prev[tabKey] || {}),
          ...queryState,
          templates: [],
          totalCount: 0,
          loading: false,
        },
      }));
      await showErrorAlert(
        "Load Failed",
        error?.response?.data?.message || "Failed to load templates.",
      );
    }
  };

  useEffect(() => {
    loadTemplates(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const updateTabState = (tabKey, patch) => {
    setTabState((prev) => ({
      ...prev,
      [tabKey]: { ...getInitialTabState(), ...(prev[tabKey] || {}), ...patch },
    }));
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  const handleSearchSubmit = () => {
    loadTemplates(activeTab, {
      search: currentState.searchInput,
      pageNumber: 1,
    });
  };

  const handleSortChange = (sortValue) => {
    loadTemplates(activeTab, { sortValue, pageNumber: 1 });
  };

  const handlePageSizeChange = (pageSize) => {
    loadTemplates(activeTab, { pageSize, pageNumber: 1 });
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    loadTemplates(activeTab, { pageNumber });
  };

  const handleToggleActive = async (tpl) => {
    const newActive = !tpl.active;
    setUpdatingId(tpl.id);

    setTabState((prev) => ({
      ...prev,
      admin: {
        ...prev.admin,
        templates: prev.admin.templates.map((t) =>
          t.id === tpl.id ? { ...t, active: newActive } : t,
        ),
      },
    }));

    try {
      const formData = buildUpdateFormData(tpl, { isActive: newActive });
      const response = await TemplateService.updateTemplate(formData);
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating template status:", error);
      setTabState((prev) => ({
        ...prev,
        admin: {
          ...prev.admin,
          templates: prev.admin.templates.map((t) =>
            t.id === tpl.id ? { ...t, active: tpl.active } : t,
          ),
        },
      }));
      await showErrorAlert("Update Failed", "Failed to update template status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (tpl, tabKey = activeTab) => {
    const confirmed = await showDeleteConfirm({
      title: "Delete template?",
      text: `Are you sure you want to delete "${tpl.name}"? This action cannot be undone.`,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!confirmed) return;

    setDeletingId(tpl.id);
    try {
      const response = await TemplateService.deleteTemplate(tpl.id);
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Delete failed");
      }

      await loadTemplates(tabKey);
      await showSuccessAlert("Deleted", `"${tpl.name}" was deleted successfully.`);
    } catch (error) {
      console.error("Error deleting template:", error);
      await showErrorAlert(
        "Delete Failed",
        error?.response?.data?.message || "Failed to delete template.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const renderAdminTab = () => (
    <TemplateTabContent
      tabKey="admin"
      tabState={tabState.admin}
      updateTabState={updateTabState}
      onSearchSubmit={handleSearchSubmit}
      onSortChange={handleSortChange}
      onPageSizeChange={handlePageSizeChange}
      onPageChange={handlePageChange}
      emptyMessage="No admin templates found."
      renderCardFooter={(tpl) => (
        <>
          <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
            <span className="small text-muted">Active</span>
            <Form.Check
              type="switch"
              id={`switch-${tpl.id}`}
              checked={tpl.active}
              disabled={updatingId === tpl.id || deletingId === tpl.id}
              onChange={() => handleToggleActive(tpl)}
              label=""
            />
          </div>
          <div className="d-flex justify-content-center gap-1">
            <Button
              variant="link"
              size="sm"
              className="p-1 text-secondary"
              title="View template"
              onClick={() => setViewTemplate(tpl)}
            >
              <GrView size={16} />
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-1 text-primary"
              title="Edit template"
              disabled={deletingId === tpl.id}
              onClick={() => onEditTemplate?.(tpl)}
            >
              <FiEdit2 size={16} />
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-1 text-danger"
              title="Delete template"
              disabled={deletingId === tpl.id}
              onClick={() => handleDelete(tpl, "admin")}
            >
              {deletingId === tpl.id ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <RiDeleteBinLine size={16} />
              )}
            </Button>
          </div>
        </>
      )}
    />
  );

  const renderSuperAdminTab = () => (
    <TemplateTabContent
      tabKey="superAdmin"
      tabState={tabState.superAdmin}
      updateTabState={updateTabState}
      onSearchSubmit={handleSearchSubmit}
      onSortChange={handleSortChange}
      onPageSizeChange={handlePageSizeChange}
      onPageChange={handlePageChange}
      emptyMessage="No sample templates available."
      renderCardFooter={(tpl) => (
        <>
          <div className="d-flex justify-content-center gap-1">
            <Button
              variant="link"
              size="sm"
              className="p-1 text-secondary"
              title="View template"
              onClick={() => setViewTemplate(tpl)}
            >
              <GrView size={16} />
            </Button>
            {isSuperAdminUser && (
              <>
                <Button
                  variant="link"
                  size="sm"
                  className="p-1 text-primary"
                  title="Edit template"
                  disabled={deletingId === tpl.id}
                  onClick={() => onEditTemplate?.(tpl)}
                >
                  <FiEdit2 size={16} />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-1 text-danger"
                  title="Delete template"
                  disabled={deletingId === tpl.id}
                  onClick={() => handleDelete(tpl, "superAdmin")}
                >
                  {deletingId === tpl.id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <RiDeleteBinLine size={16} />
                  )}
                </Button>
              </>
            )}
          </div>
          {!isSuperAdminUser && (
            <PrimaryButton
              className="w-100 mt-1"
              style={{ borderRadius: 8, fontSize: "0.875rem", padding: "0.5rem 1rem" }}
              onClick={() => onSelectSample?.(tpl)}
            >
              Use Template
            </PrimaryButton>
          )}
        </>
      )}
    />
  );

  return (
    <Container fluid className="page-top pb-4">
      <Row className="mb-4">
        <Col md={8}>
          <h2 className="page-title mb-2">Template Management</h2>
          <p className="page-subtitle mb-0">
            Manage your team&apos;s admin templates, or browse sample available templates to
            customize and reuse.
          </p>
        </Col>
        <Col md={4} className="d-flex justify-content-md-end align-items-start">
          {activeTab === "admin" && (
            <PrimaryButton onClick={onCreateNew}>Create New Template</PrimaryButton>
          )}
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <NurturixCustomTabs
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          {activeTab === "admin" ? renderAdminTab() : renderSuperAdminTab()}
        </Col>
      </Row>

      <TemplateViewModal
        template={viewTemplate}
        show={!!viewTemplate}
        onHide={() => setViewTemplate(null)}
        onUseTemplate={onSelectSample}
        showUseButton={activeTab === "superAdmin" && !isSuperAdminUser}
      />
    </Container>
  );
};

export default TemplateListing;
